// StatCards — small primitive used by DetailPage status cards row + (later)
// ListPage stats. Slim by design (CHG-1225).
//
// Per dashboard-v2-tokens.md §3 token system + tint-<group> classes.

import type { ReactNode } from "react";
import { useFormatters } from "../../contexts/IntlContext";
import type { ToneToken } from "../../lib/stage-tone-heuristic";

export type StatCardFormat =
  | "currency"
  | "number"
  | "percent"
  | "text"
  | "date"
  // CHG-1314 — added so the v2-gen generator can emit asset.file_size
  // (bytes) + asset.last_used / donation.recorded (relative-date)
  // declaratively, replacing the inline `render:` overrides that
  // previously lived in asset-detail.ts + donation-detail.ts.
  // See specs/_product/dashboard-v2-page-generator.md §11.
  | "bytes"
  | "relative-date"
  | "badge";

export interface StatCardDef {
  id: string;
  label: string;
  value: number | string | null | undefined;
  format?: StatCardFormat;
  /** Optional tone for a left-edge accent stripe. Maps to a group gradient. */
  tone?: ToneToken;
  /** Optional currency code when format === 'currency' (default USD). */
  currency?: string;
  /** Optional rendered ReactNode override (used for badge format). */
  render?: ReactNode;
}

export interface StatCardsProps {
  cards: StatCardDef[];
  loading?: boolean;
  /** Number of columns; defaults to count(cards) up to 4. */
  columns?: number;
}

export function StatCards({ cards, loading, columns }: StatCardsProps) {
  const fmt = useFormatters();
  const cols = columns ?? Math.min(cards.length, 4);

  if (loading) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: 12,
          marginBottom: 20,
        }}
      >
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="card" style={{ height: 92, opacity: 0.5 }} />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 12,
        marginBottom: 20,
      }}
    >
      {cards.map((c) => (
        <div
          key={c.id}
          className="card"
          style={{
            padding: 16,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {c.tone ? (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 0,
                top: 12,
                bottom: 12,
                width: 3,
                borderRadius: 2,
                background: `var(--color-${c.tone}-from)`,
              }}
            />
          ) : null}
          <div
            style={{
              fontSize: 11,
              color: "var(--color-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 6,
              paddingLeft: c.tone ? 8 : 0,
            }}
          >
            {c.label}
          </div>
          <div
            className="tabular"
            style={{
              fontSize: 22,
              fontWeight: 500,
              color: "var(--color-text)",
              lineHeight: 1.1,
              paddingLeft: c.tone ? 8 : 0,
            }}
          >
            {c.render ?? formatCardValue(c, fmt)}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatCardValue(c: StatCardDef, fmt: ReturnType<typeof useFormatters>): string {
  if (c.value === null || c.value === undefined) return "—";
  switch (c.format) {
    case "currency":
      return fmt.formatCurrency(Number(c.value), c.currency);
    case "number":
      return fmt.formatNumber(Number(c.value));
    case "percent":
      return fmt.formatPercent(Number(c.value));
    case "date":
      return fmt.formatDate(String(c.value), "medium");
    case "relative-date":
      // CHG-1314 — useFormatters().formatDate supports "relative" style;
      // this case lets the generator emit format: "relative-date"
      // instead of a render: override.
      return fmt.formatDate(String(c.value), "relative");
    case "bytes":
      // CHG-1314 — local formatter (bytes is developer-facing, not
      // locale-sensitive). Mirrors the formatBytes helper that
      // previously lived inline in asset-detail.ts.
      return formatBytes(Number(c.value));
    case "text":
    case "badge":
    default:
      return String(c.value);
  }
}

/** CHG-1314 — bytes count → "B / KB / MB / GB" with 1 decimal. */
function formatBytes(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
